import { useGetBinaryDocument } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { useGetPatientByUpid } from '@ctw/shared/api/fhir/patient';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { SearchCombobox } from '@ctw/shared/components/form/search-combobox';
import { Heading } from '@ctw/shared/components/heading';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { downloadBlob } from '@ctw/shared/content/CCDA/binary-field';
import { CdaParser } from '@ctw/shared/content/document-viewer/_internal/parsers/cda-parser';
import { PdfCdaRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-cda-renderer';
import { DocumentViewerControlButton } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-button';
import { DocumentViewerControlLink } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-link';
import { DocumentViewerControlMenu } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-menu';
import {
  type DocumentViewerDetailField,
  DocumentViewerDetailFields,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-detail-fields';
import {
  type DocumentViewerHeaderField,
  DocumentViewerHeaderFields,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-header-fields';
import {
  type DocumentViewerTab,
  DocumentViewerTabMenu,
  DocumentViewerTabs,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-tab-menu';
import { DocumentRenderer } from '@ctw/shared/content/document-viewer/document-renderer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery, useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import {
  DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  faArrowUpRightFromSquare,
  faFileDownload,
  faPrint,
} from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faX } from '@fortawesome/pro-solid-svg-icons';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { compact } from 'lodash-es';
import { type MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

interface DocumentViewerProps {
  documentReference: DocumentModel;
  onClose: () => void;
  controls: {
    close: boolean;
    openInNewTab: boolean;
    print: boolean;
    download: boolean;
    search: boolean;
  };
}

export const DocumentViewer = ({ documentReference, onClose, controls }: DocumentViewerProps) => {
  const { service } = useZuiContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { breakpoints } = useContainerQuery({ containerRef });
  const hasDocumentViewerV2Search = useFeatureFlag('ctw-document-viewer-v2-search');
  const { breakpoints: screenBreakpoints } = useBreakpointQuery();
  const patientContext = usePatientContextIfAvailable();
  const patientQuery = useGetPatientByUpid({
    upid: documentReference.patientUPID ?? '',
    enabled: !patientContext,
  });
  const patient = useMemo(
    () => (patientContext ? patientContext.patient : patientQuery.data),
    [patientContext, patientQuery.data],
  );
  const binaryQuery = useGetBinaryDocument(
    documentReference.binaryId ?? '',
    documentReference.binaryId !== undefined,
  );

  const isXmlDocument = useMemo(
    () => documentReference.contentType?.includes('xml'),
    [documentReference],
  );

  const isLoading = useMemo(
    () => binaryQuery.isLoading || patientQuery.isLoading,
    [binaryQuery.isLoading, patientQuery.isLoading],
  );

  const boundaryContext = useMemo(
    () => ({
      documentPatientUpid: patient?.UPID,
      documentReferenceId: documentReference.id,
      documentBinaryId: binaryQuery.data?.binary?.id,
      documentContentType: binaryQuery.data?.binary.contentType,
    }),
    [patient, documentReference.id, binaryQuery.data],
  );

  const tabs: Array<DocumentViewerTab> = useMemo(
    (): Array<DocumentViewerTab> =>
      [
        {
          id: 'document',
          label: 'Document',
        },
        {
          id: 'details',
          label: 'Details',
        },
      ] satisfies Array<DocumentViewerTab>,
    [],
  );

  const [selectedTab, setSelectedTab] = useState<DocumentViewerTab>(tabs[0]);

  const downloadRaw = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (binaryQuery.data) {
        downloadBlob(binaryQuery.data.binary.blob, `document-${binaryQuery.data.binary.id}`);
      }
    },
    [binaryQuery.data],
  );

  const downloadPdf = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (binaryQuery.data?.binary?.data) {
        const cdaParser = new CdaParser(binaryQuery.data?.binary?.data);
        const pdfData = pdf(<PdfCdaRenderer cdaParser={cdaParser} />);
        void pdfData.toBlob().then((blob) => {
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          const url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download = `${documentReference.title} (Zus ${format(new Date(), 'dd/MM/yyyy')}).pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        });
      }
    },
    [documentReference, binaryQuery],
  );

  const printDocument = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (binaryQuery.data?.binary?.data) {
        const cdaParser = new CdaParser(binaryQuery.data?.binary?.data);
        const pdfData = pdf(<PdfCdaRenderer cdaParser={cdaParser} />);
        void pdfData.toBlob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const iframe = document.createElement('iframe');
          iframe.style = 'display: none';
          iframe.src = url;
          iframe.onload = () => {
            iframe.contentWindow?.print();
            window.URL.revokeObjectURL(url);
          };
          document.body.appendChild(iframe);
        });
      }
    },
    [binaryQuery],
  );

  const headerFields: Array<DocumentViewerHeaderField> = useMemo(
    () =>
      compact([
        {
          label: 'Encounter Type',
          value: 'Unknown', // TODO
        },
        {
          label: 'Date',
          value: documentReference.encounterDate ?? 'Unknown',
        },
        {
          label: 'Author',
          value: documentReference.custodian ?? 'Unknown',
        },
      ]) satisfies Array<DocumentViewerHeaderField>,
    [documentReference],
  );

  const detailFields: Array<DocumentViewerDetailField> = useMemo(
    () =>
      compact([
        ...((patient
          ? compact([
              {
                label: 'Patient Name',
                value: `${patient.firstName} ${patient.lastName} [${patient.gender?.[0]}]`,
              },
              {
                label: 'DOB',
                value: patient.dob ?? 'Unknown',
              },
              {
                label: 'Phone',
                value: patient.phoneNumber ?? 'Unknown',
              },
              {
                label: 'Email',
                value: patient.email ?? 'Unknown',
              },
            ])
          : []) satisfies Array<DocumentViewerDetailField>),
        {
          label: 'Document Title',
          value: documentReference.title,
        },
        {
          label: 'Encounter Type',
          value: 'Unknown', // TODO
        },
        {
          label: 'Date',
          value: documentReference.encounterDate ?? 'Unknown',
        },
        {
          label: 'Author',
          value: documentReference.custodian ?? 'Unknown',
        },
      ]) satisfies Array<DocumentViewerDetailField>,
    [patient, documentReference],
  );

  if (isLoading) {
    return (
      <article
        className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden`}
      >
        <LoadingSpinner message="Loading document..." centered={true} />
      </article>
    );
  }

  if (!(patient && binaryQuery.data?.binary)) {
    return (
      <article
        className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden`}
      >
        <ContentError title="Unable to load this document">
          <span>If this problem persists </span>
          <SupportFormLink buttonText="contact support" />.
        </ContentError>
      </article>
    );
  }

  return (
    <ErrorBoundary boundaryName="DocumentViewer" initialContext={boundaryContext}>
      {() => (
        <div
          className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden bg-white`}
        >
          <div
            className={tw`flex w-full min-w-full max-w-full flex-wrap items-center justify-between gap-4 overflow-hidden bg-content-main px-5 pt-4`}
          >
            <div
              className={tw`-ml-2 flex min-h-14 w-fit max-w-fit items-center justify-between gap-2`}
            >
              {controls.close && (
                <DocumentViewerControlButton
                  label={screenBreakpoints.isAtLeast.lg ? 'Close' : 'Back'}
                  testId={
                    screenBreakpoints.isAtLeast.lg
                      ? DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID
                      : DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID
                  }
                  icon={screenBreakpoints.isAtLeast.lg ? faX : faArrowLeft}
                  telemetryInteractionName="document_viewer_control_click_close"
                  onClick={() => onClose?.()}
                />
              )}
              <Heading
                level="h1"
                className={tw`overflow-hidden text-ellipsis whitespace-nowrap font-medium text-white`}
              >
                {documentReference.title}
              </Heading>
            </div>
            <div className={tw`flex min-h-14 flex-1 items-center gap-1`}>
              {controls.openInNewTab && service === 'standalone' && (
                <DocumentViewerControlLink
                  label="Open in new Tab"
                  icon={faArrowUpRightFromSquare}
                  testId={DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID}
                  target="_blank"
                  href={`/patient/${patient.UPID}/document/${documentReference.id}`}
                  telemetryInteractionName="document_viewer_control_click_open_in_new_tab"
                />
              )}
              {controls.print && (
                <DocumentViewerControlButton
                  label="Print"
                  icon={faPrint}
                  testId={DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID}
                  onClick={printDocument}
                  telemetryInteractionName="document_viewer_control_click_print"
                />
              )}
              {controls.download &&
                (isXmlDocument ? (
                  <DocumentViewerControlMenu
                    label="Download"
                    icon={faFileDownload}
                    testId={DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID}
                    controls={[
                      {
                        label: 'Download XML',
                        testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
                        telemetryInteractionName: 'document_viewer_control_click_download_raw',
                        onClick: downloadRaw,
                      },
                      {
                        label: 'Download PDF',
                        testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
                        telemetryInteractionName: 'document_viewer_control_click_download_pdf',
                        onClick: downloadPdf,
                      },
                    ]}
                  />
                ) : (
                  <DocumentViewerControlButton
                    label="Download"
                    testId={DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID}
                    icon={faFileDownload}
                    onClick={downloadRaw}
                    telemetryInteractionName="document_viewer_control_click_download_raw"
                  />
                ))}
              {hasDocumentViewerV2Search && isXmlDocument && (
                <SearchCombobox
                  className={tw`flex-1`}
                  inputClassName={tw`bg-white`}
                  enableSearchIcon={true}
                  placeholder=""
                  options={[]}
                  readonly={false}
                  isLoading={true}
                  name="document-search"
                  onCustomSelectChange={() => alert('hi')}
                  renderCustomOption={() => <div>foop</div>}
                  onSearchChange={(_value) => alert('hi')}
                  usesSearchButton={false}
                />
              )}
            </div>
            <DocumentViewerHeaderFields fields={headerFields} />
            {breakpoints.isAtLeast.sm ? (
              <DocumentViewerTabs
                tabs={tabs}
                selectedTab={selectedTab}
                onTabSelected={(id) => setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])}
              />
            ) : (
              <div className={tw`w-full px-4 pb-4`}>
                <DocumentViewerTabMenu
                  tabs={tabs}
                  selectedTab={selectedTab}
                  onTabSelected={(id) =>
                    setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])
                  }
                />
              </div>
            )}
          </div>
          <div
            ref={containerRef}
            className={tw`flex h-full max-h-full w-full flex-1 overflow-hidden`}
          >
            <div
              className={twx('hidden', {
                contents: selectedTab.id === 'document',
              })}
            >
              <DocumentRenderer document={documentReference} binaryData={binaryQuery.data} />
            </div>
            <div
              className={twx('hidden', {
                contents: selectedTab.id === 'details',
              })}
            >
              <div className={tw`flex flex-col gap-5 p-5`}>
                <Heading level="h2">Document Details</Heading>
                <DocumentViewerDetailFields fields={detailFields} />
              </div>
            </div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};
